// web/src/routes/dashboard/ManagerDashboard.jsx
// Today. Answers: "Is the barn running?"
//
// Laid out like a planner page, because that is what it is — a Franklin or a
// Day-Timer opens to the little month in the corner, the appointments down one
// side and the prioritised list down the other, and a barn already thinks in
// exactly those terms. The availability calendar reads that way and it is the
// part of the app that felt right, so it sets the idiom: pale green ground,
// hunter ink, hairline rules, every section wearing a small capital label.
//
// The rule that matters more than the styling: EVERY ROW GOES TO ITS RECORD.
// Before this, the whole card was one link to a generic index — clicking a
// horse's name took you to the list of all horses, which is the one place you
// already knew how to get to. A dashboard whose rows are not links is a report,
// and nobody needs a report about their own morning.
//
// That needed the API to change, not just the markup: the payload sent
// horse_name and no horse_id, so the link had nowhere to point.

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { fmtDate, fmtStamp } from '../../lib/dates';
import { colorClass } from '../../lib/lessonTypes';
import { timeRange, clock } from '../../lib/shifts';
import MiniMonth from '../../components/dashboard/MiniMonth';
import {
  DayHeader, Panel, Empty, Rows, PlannerGrid, PlannerSkeleton,
} from '../../components/dashboard/Planner';

export default function ManagerDashboard() {
  // The day being read lives in the URL, so a particular day is a link and the
  // back button walks back through the days you turned to.
  const [params, setParams] = useSearchParams();
  const date = params.get('date');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'manager', date ?? 'today'],
    queryFn: () => api.get(date ? `/dashboard/manager?date=${date}` : '/dashboard/manager'),
    // The previous day stays on screen while the next one loads. A planner page
    // that blanks between days flickers on every tap of the calendar.
    placeholderData: (prev) => prev,
  });

  const today = data?.today ?? null;
  const shown = data?.date ?? date ?? today;

  const goTo = (d) => {
    const next = new URLSearchParams(params);
    if (!d || d === today) next.delete('date');
    else next.set('date', d);
    setParams(next, { replace: true });
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <DayHeader
        today={today}
        date={shown}
        onToday={() => goTo(null)}
        action={
          <Link to={`/schedule?view=day&date=${shown ?? ''}`} className="text-sm text-hunter hover:underline pb-1">
            Open the board →
          </Link>
        }
      />

      {isLoading ? (
        <PlannerSkeleton />
      ) : (
        <PlannerGrid
          aside={
            <>
              {/* Tapping a date turns the page to it rather than leaving for
                  the schedule — the whole point of a planner is that the day
                  you flip to is still a planner page. */}
              <MiniMonth today={today} selected={shown} onPick={goTo} />
              <TaskProgress data={data?.taskProgress} />
              <OnDuty items={data?.onDuty} />
            </>
          }
        >
          <Appointments items={data?.appointments} today={shown} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <Overdue items={data?.overdue} />
            <HealthDue items={data?.healthDue} />
          </div>
          <RecentNotes items={data?.recentActivity} />
        </PlannerGrid>
      )}
    </div>
  );
}

function TaskProgress({ data }) {
  const done = data?.done ?? 0;
  const total = data?.total ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <Panel title="Today's tasks">
      <Link to="/board" className="planner-row">
        <span className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold text-hunter tabular-nums">{done}</span>
          <span className="text-ink-500">of {total} done</span>
        </span>
        <span className="mt-2 block h-1.5 bg-hunter/10 rounded-full overflow-hidden">
          <span className="block h-full bg-hunter transition-all" style={{ width: `${pct}%` }} />
        </span>
      </Link>
    </Panel>
  );
}

function Appointments({ items = [], today }) {
  return (
    <Panel
      title="Appointment schedule"
      action={
        <Link to={`/schedule?view=day&date=${today ?? ''}`} className="normal-case tracking-normal hover:underline">
          the whole day →
        </Link>
      }
    >
      {items.length === 0 ? (
        <Empty>Nothing booked today.</Empty>
      ) : (
        <Rows>
          {items.map((a) => (
            <li key={a.id}>
              {/* To the horse, because that is the record behind a ride. The
                  rider is a person and the lesson is an event, but the horse is
                  the thing you go and look at. */}
              <Link to={a.horseId ? `/horses/${a.horseId}` : '/schedule'} className="planner-row flex items-baseline gap-3">
                <span className="w-16 shrink-0 tabular-nums text-hunter font-medium">
                  {a.startTime ? clock(a.startTime).replace(':00', '') : 'TBD'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    {a.typeName && (
                      <span className={'inline-block w-2 h-2 rounded-full mr-1.5 ' + colorClass(a.color, 'dot')} />
                    )}
                    <span className="font-medium">{a.riderName}</span>
                    {a.horseName && <span className="text-ink-500"> · {a.horseName}</span>}
                  </span>
                  <span className="block text-xs text-ink-400">
                    {[a.typeName, a.trainerName].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function OnDuty({ items = [] }) {
  return (
    <Panel title="On today" count={items.length || null}>
      {items.length === 0 ? (
        <Empty>
          Nobody rostered. <Link to="/schedule/shifts" className="text-hunter hover:underline">Build the week →</Link>
        </Empty>
      ) : (
        <Rows>
          {items.map((s) => (
            <li key={s.id}>
              <Link to="/schedule/shifts?view=day" className="planner-row flex items-baseline gap-2">
                <span className={'w-2 h-2 rounded-full shrink-0 ' + colorClass(s.color, 'dot')} />
                <span className="min-w-0 flex-1 truncate">
                  <span className={s.name ? 'font-medium' : 'text-amber-700'}>{s.name || 'Open shift'}</span>
                  {s.typeName && <span className="text-ink-500"> · {s.typeName}</span>}
                </span>
                <span className="shrink-0 text-xs text-ink-400 tabular-nums">
                  {timeRange(s.startTime, s.endTime)}
                </span>
              </Link>
            </li>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function Overdue({ items = [] }) {
  return (
    <Panel title="Overdue" count={items.length || null}>
      {items.length === 0 ? (
        <Empty>Nothing overdue.</Empty>
      ) : (
        <Rows>
          {items.map((t) => (
            <li key={t.id}>
              <Link to={t.horseId ? `/horses/${t.horseId}` : '/board'} className="planner-row flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{t.horseName}</span>
                  <span className="text-ink-500"> · {t.taskType}</span>
                </span>
                {/* Was a raw "2026-09-01" baked into a label string on the
                    server. It travels as a date now, so it can be read. */}
                <span className="shrink-0 text-xs text-rose-700 tabular-nums">{fmtDate(t.date, 'short')}</span>
              </Link>
            </li>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function HealthDue({ items = [] }) {
  return (
    <Panel title="Health items due" count={items.length || null}>
      {items.length === 0 ? (
        <Empty>All clear for the next 30 days.</Empty>
      ) : (
        <Rows>
          {items.map((h) => (
            <li key={h.id}>
              <Link to={h.horseId ? `/horses/${h.horseId}` : '/horses'} className="planner-row flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{h.horseName}</span>
                  <span className="text-ink-500"> · {h.recordType}</span>
                </span>
                <span className="shrink-0 text-xs text-ink-400 tabular-nums">{fmtDate(h.dueDate, 'short')}</span>
              </Link>
            </li>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function RecentNotes({ items = [] }) {
  return (
    <Panel title="Recent notes" count={items.length || null}>
      {items.length === 0 ? (
        <Empty>No notes this week.</Empty>
      ) : (
        <Rows>
          {items.map((n) => (
            <li key={n.id}>
              <Link to={n.horseId ? `/horses/${n.horseId}` : '/horses'} className="planner-row">
                <span className="flex items-baseline justify-between gap-2">
                  <span className="font-medium truncate">{n.horseName}</span>
                  <span className="shrink-0 text-xs text-ink-400">
                    {n.authorName}
                    {n.createdAt ? ` · ${fmtStamp(n.createdAt, 'short')}` : ''}
                  </span>
                </span>
                <span className="block text-ink-600 line-clamp-2">{n.body}</span>
              </Link>
            </li>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

